import { useLayoutEffect, useRef, useState } from "react";

import { AttributeType } from "@interfaces/attribute.interface";

import { Input } from "./Input";
import { MASK_INPUT_VARIANTS, MaskInputVariant } from "./MaskInput.config";
import {
  getMaskInputDisplayValue,
  getMaskInputSuffix,
  sanitizeMaskInputValue,
} from "./MaskInput.util";

/** Left border (1px) + the input's pl-3, i.e. where it renders its first character. */
export const INPUT_TEXT_OFFSET_PX = 13;

/** Breathing room before a trailing unit — "213.23 mm" rather than "213.23mm". */
export const SUFFIX_GAP_PX = 3;

/** Height of the field row: 2px border + py-2 + the text-sm line box. */
const FIELD_HEIGHT_CLASS = "h-[38px]";

const MEASURED_TEXT_CLASS = "whitespace-pre font-inter text-sm font-normal";

interface MaskInputProps {
  variant: MaskInputVariant;
  value: string;
  onChange: (value: string) => void;
  /** Overrides the variant's leading prefix — the entity's currency symbol, in practice. */
  prefix?: string;
  disabled?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  onFocus?: () => void;
  onBlur?: () => void;
}

/**
 * Numeric attribute field. Everything type-specific — placeholder, prefix/suffix, bounds, decimals
 * — comes from MASK_INPUT_VARIANTS, and the field itself is argo's `Input`, so the border, focus
 * ring, dark theme and error text stay identical to every other input.
 */
export function MaskInput({
  variant,
  value,
  onChange,
  prefix,
  disabled,
  isInvalid,
  errorMessage,
  onFocus,
  onBlur,
}: MaskInputProps) {
  const config = MASK_INPUT_VARIANTS[variant];
  const [isFocused, setIsFocused] = useState(false);

  const prefixRef = useRef<HTMLSpanElement>(null);
  const measuredTextRef = useRef<HTMLSpanElement>(null);
  const [prefixWidth, setPrefixWidth] = useState(0);
  const [textWidth, setTextWidth] = useState(0);

  const leadingPrefix = prefix ?? config.prefixes.initial;
  const displayValue = getMaskInputDisplayValue(value, variant, isFocused);
  const suffix = getMaskInputSuffix(value, variant, isFocused);
  const measuredText = displayValue || config.placeholder;

  useLayoutEffect(() => {
    setPrefixWidth(prefixRef.current?.offsetWidth ?? 0);
  }, [leadingPrefix]);

  useLayoutEffect(() => {
    setTextWidth(measuredTextRef.current?.offsetWidth ?? 0);
  }, [measuredText]);

  const textStartPx = INPUT_TEXT_OFFSET_PX + prefixWidth;

  const handleChange = (next: string) => {
    const sanitized = sanitizeMaskInputValue(next, variant);

    if (sanitized !== null) onChange(sanitized);
  };

  return (
    <div className="relative w-full">
      <Input
        isFullWidth
        // Text rather than number: the value carries separators and a suffix.
        type="text"
        inputMode={variant === AttributeType.CREDIT ? "text" : "decimal"}
        value={displayValue}
        placeholder={config.placeholder}
        disabled={disabled}
        isInvalid={isInvalid}
        errorMessage={errorMessage}
        StartIcon={
          leadingPrefix ? (
            <span ref={prefixRef} className="text-sm text-content-disabled">
              {leadingPrefix}
            </span>
          ) : undefined
        }
        // Clears the measured prefix instead of the StartIcon default of pl-10.
        inputStyle={leadingPrefix ? { paddingLeft: textStartPx } : undefined}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => {
          setIsFocused(true);
          onFocus?.();
        }}
        onBlur={() => {
          setIsFocused(false);
          onBlur?.();
        }}
      />

      {/* Mirrors the field's text so the suffix can sit right after it, as it does in the CRM. */}
      <span
        ref={measuredTextRef}
        aria-hidden
        className={`invisible absolute left-0 top-0 ${MEASURED_TEXT_CLASS}`}
      >
        {measuredText}
      </span>
      <span
        aria-hidden
        className={`pointer-events-none absolute top-0 flex items-center text-content-disabled ${FIELD_HEIGHT_CLASS} ${MEASURED_TEXT_CLASS}`}
        style={{ left: `${textStartPx + textWidth + SUFFIX_GAP_PX}px` }}
      >
        {suffix}
      </span>
    </div>
  );
}
